/**
 * Image utility functions
 */

import { API_CONFIG } from "@/api/core/api.config";

/**
 * Build absolute image URL from relative or absolute path
 * @param url - The image URL (relative or absolute)
 * @returns Absolute URL or empty string if invalid
 */
export function getImageUrl(url: string | undefined | null): string {
  // Return empty string for invalid URLs
  if (!url || typeof url !== "string" || url.trim() === "") {
    return "";
  }

  try {
    // If already absolute URL, return as is
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    // Build absolute URL from base
    const base =
      process.env.NEXT_PUBLIC_IMG_URL ||
      process.env.NEXT_PUBLIC_CMS_URL ||
      API_CONFIG.IMG_URL ||
      "";

    if (!base) {
      // If no base URL configured, return the original URL
      // This might work for URLs relative to the current domain
      return url;
    }

    // Ensure base doesn't end with slash and url doesn't start with slash
    const cleanBase = base.replace(/\/$/, "");
    const cleanUrl = url.replace(/^\//, "");

    return `${cleanBase}/${cleanUrl}`;
  } catch (error) {
    console.error("Error building image URL:", error);
    return url; // Return original URL as fallback
  }
}

/**
 * Check if an image URL is valid and not empty
 * @param url - The image URL to validate
 * @returns True if URL is valid
 */
export function isValidImageUrl(url: string | undefined | null): boolean {
  if (!url || typeof url !== "string" || url.trim() === "") {
    return false;
  }

  // Check for common invalid patterns
  if (url === "null" || url === "undefined" || url === '""' || url === "''") {
    return false;
  }

  // Basic URL validation
  try {
    // Try to create a URL object (works for absolute URLs)
    new URL(url);
    return true;
  } catch {
    // If not absolute, check if it looks like a valid relative path
    return /^[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=]+$/.test(url);
  }
}

/**
 * Get image URL with fallback
 * @param url - Primary image URL
 * @param fallback - Fallback image URL
 * @returns Valid image URL or fallback
 */
export function getImageUrlWithFallback(
  url: string | undefined | null,
  fallback: string = ""
): string {
  const primaryUrl = getImageUrl(url);

  if (isValidImageUrl(primaryUrl)) {
    return primaryUrl;
  }

  return fallback;
}

/**
 * Generate placeholder image URL
 * @param width - Image width
 * @param height - Image height
 * @param text - Text to display on placeholder
 * @returns Placeholder image URL
 */
export function getPlaceholderImage(
  width: number = 100,
  height: number = 100,
  text: string = "Image"
): string {
  // Using placeholder.com service
  return `https://via.placeholder.com/${width}x${height}?text=${encodeURIComponent(
    text
  )}`;
}
